import type { AttachmentPickers, ReadOnlyRibbonControlDefProps } from 'owa-mail-ribbon-utils';
import type { RibbonTabDefinitionBase } from 'owa-acui';
import type { RibbonDefinitionResult } from 'owa-command-ribbon';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { owaComputedFn } from 'owa-computed-fn';
import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import getMLRHomeTab from './mlr-home-tab/getMLRHomeTab';
import getMLRNotesTab from './mlr-notes-tab/getMLRNotesTab';
import getMLRViewTab from './mlr-view-tab/getMLRViewTab';
import getMLRSearchToolsTab from './mlr-search-tab/getMLRSearchToolsTab';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isNotesScenario } from 'owa-mail-ribbon-utils/lib/isNotesScenario';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import { getHelpTab } from 'owa-ribbon-help';
import { getSelectedTab } from 'owa-mail-ribbon-store/lib/selectors/getSelectedTab';
import type { MailboxInfo } from 'owa-client-types';
import { lazyGetMLRComposeTabs } from '../../lazyComposeFunctions';
import getComposePlaceholderTabs from '../../util/getComposePlaceholderTabs';
import getIsSearchTableShown from 'owa-mail-list-store/lib/utils/getIsSearchTableShown';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { isCopilotResultsTable } from 'owa-mail-list-store/lib/store/schema/CopilotResultsTableQuery';
import { type PerformanceDatapoint } from 'owa-analytics';
import { type ComposePropsCache, getOrUpdateComposeProps } from '../utils/getOrUpdateComposeProps';

const composePropsCache: ComposePropsCache = {
    composePropsGlobal: undefined,
    prevAttachmentPickers: undefined,
    prevViewState: undefined,
};

let stableResult: RibbonDefinitionResult | null = null;

// Clarification: "readOnly" does not mean that this property never has its properties changed.
// "readOnly" in this context means the mail state, so "not compose".
// Importantly, mailboxInfo can change and we track that change.
let readOnlyPropsGlobal: ReadOnlyRibbonControlDefProps = {
    isPopout: false,
    mailboxInfo: undefined,
    itemId: undefined,
    projectionTabId: undefined,
    selectedRowKey: undefined,
};

function buildMLRComposeResult(
    viewState: ComposeViewState,
    attachmentPickers: AttachmentPickers
): {
    tabs: RibbonTabDefinitionBase[];
    scenarioDatapoint?: PerformanceDatapoint;
} {
    const composeProps = getOrUpdateComposeProps(composePropsCache, viewState, attachmentPickers);

    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const getMLRComposeTabsFn = lazyGetMLRComposeTabs.tryImportForRender();
    const tabs = getMLRComposeTabsFn
        ? getMLRComposeTabsFn(composeProps)
        : getComposePlaceholderTabs(viewState.editorId, true /* isMlr */);

    return {
        tabs,
        scenarioDatapoint: viewState.perfDatapoint,
    };
}

export const getMailRibbonDefinitionsMLR: (
    attachmentPickers: AttachmentPickers
) => RibbonDefinitionResult = owaComputedFn(function getMailRibbonDefinitionsMLR(
    attachmentPickers: AttachmentPickers
): RibbonDefinitionResult {
    const tabs: RibbonTabDefinitionBase[] = [];

    // Check if we need to update readOnlyProps for Mailbox Info
    const mailboxInfo: MailboxInfo = getRibbonMailboxInfo(
        undefined /* RibbonControlDefinitionProps */
    );

    const viewState: ComposeViewState | undefined = getComposeViewState();
    /* When compose has started opening but hasn't rendered yet, return the last stable
       result (same object reference). Because owaComputedFn/computedFn uses reference
       equality, returning the same reference suppresses propagation to CommandRibbon,
       eliminating the intermediate re-render caused by Home/View tab computeds changing
    */
    if (
        isGlobalFeatureEnabled('cmp-ribbon-compose-opt') &&
        viewState !== undefined &&
        !viewState.isComposeRendered &&
        stableResult !== null
    ) {
        return stableResult;
    }

    if (mailboxInfo !== readOnlyPropsGlobal.mailboxInfo) {
        readOnlyPropsGlobal = {
            ...readOnlyPropsGlobal,
            mailboxInfo,
        };
    }

    tabs.push(
        getMLRHomeTab(readOnlyPropsGlobal),
        getMLRViewTab(readOnlyPropsGlobal),
        getHelpTab(getSelectedTab())
    );

    if (viewState?.isComposeRendered) {
        const composeResult = buildMLRComposeResult(viewState, attachmentPickers);
        tabs.push(...composeResult.tabs);
    }

    if (isNotesScenario()) {
        tabs.push(getMLRNotesTab(readOnlyPropsGlobal));
    }
    // Add Search Tools tab when in search mode or copilot results table (but not for message suggestions - direct navigation)
    if (
        (getIsSearchTableShown() || isCopilotResultsTable(getSelectedTableView()?.tableQuery)) &&
        isGlobalFeatureEnabled('sea-searchRibbon')
    ) {
        const tableView = getSelectedTableView();
        const scenarioType = (tableView?.tableQuery as any)?.scenarioType;
        if (scenarioType !== 'messageSuggestion') {
            tabs.push(getMLRSearchToolsTab(readOnlyPropsGlobal));
        }
    }

    const result: RibbonDefinitionResult = {
        tabs,
        scenarioDatapoint: viewState?.perfDatapoint,
    };

    if (isGlobalFeatureEnabled('cmp-ribbon-compose-opt')) {
        // Update the stable cache whenever compose is NOT in a launching state (no viewState at all or compose fully launched)
        const isComposeNotLaunching = !viewState || viewState.isComposeRendered;
        if (isComposeNotLaunching) {
            stableResult = result;
        }
    }

    return result;
});
